import { useState } from 'react';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { toast } from 'sonner';
import { ArrowUpRight, Lock, Mail, User as UserIcon, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

export default function LoginModal({ open, onOpenChange, defaultTab = 'login' }) {
  const { login, signup } = useAuth();
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPw, setLoginPw] = useState('');
  const [suName, setSuName] = useState('');
  const [suEmail, setSuEmail] = useState('');
  const [suPw, setSuPw] = useState('');
  const [busy, setBusy] = useState(false);

  const errorMessage = (err) => {
    const d = err?.response?.data?.detail;
    if (typeof d === 'string') return d;
    if (Array.isArray(d)) {
      // FastAPI/Pydantic validation errors -> array of { msg, loc, ... }
      return d.map((e) => e?.msg || 'Invalid value').join(', ');
    }
    if (d && typeof d === 'object') return d.msg || 'Something went wrong';
    return err?.message || 'Something went wrong';
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    if (!loginEmail || !loginPw) {
      toast.error('Please enter your email and password');
      return;
    }
    setBusy(true);
    try {
      const u = await login(loginEmail, loginPw);
      toast.success(`Welcome back, ${u.name.split(' ')[0]}`);
      onOpenChange(false);
      setLoginEmail('');
      setLoginPw('');
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    if (!suName || !suEmail || !suPw) {
      toast.error('Please fill in every field');
      return;
    }
    if (suPw.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    setBusy(true);
    try {
      const u = await signup(suName, suEmail, suPw);
      toast.success(`Account created. Welcome, ${u.name.split(' ')[0]}`);
      onOpenChange(false);
      setSuName('');
      setSuEmail('');
      setSuPw('');
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[440px] bg-[#F5F1E8] border-[#0B3B2E]/15 p-0 overflow-hidden">
        <div className="p-8 pb-2">
          <DialogTitle asChild>
            <h2 className="font-serif text-[28px] leading-tight text-[#0B3B2E]">
              Welcome to Vector<span className="text-[#C8944B]">FX</span>
            </h2>
          </DialogTitle>
          <DialogDescription className="text-[#0E1F1A]/60 mt-1">
            Sign in or open an account in under 3 minutes.
          </DialogDescription>
        </div>

        <Tabs defaultValue={defaultTab} className="px-8 pb-8">
          <TabsList className="bg-[#0B3B2E]/5 w-full grid grid-cols-2 rounded-full h-10 p-1">
            <TabsTrigger
              value="login"
              className="rounded-full data-[state=active]:bg-[#0B3B2E] data-[state=active]:text-[#F5F1E8] data-[state=active]:shadow-none"
            >
              Log in
            </TabsTrigger>
            <TabsTrigger
              value="signup"
              className="rounded-full data-[state=active]:bg-[#0B3B2E] data-[state=active]:text-[#F5F1E8] data-[state=active]:shadow-none"
            >
              Open account
            </TabsTrigger>
          </TabsList>

          <TabsContent value="login" className="pt-6">
            <form onSubmit={handleLogin} className="space-y-4">
              <Field id="login-email" icon={<Mail className="w-4 h-4" />} label="Email" type="email"
                value={loginEmail} onChange={setLoginEmail} placeholder="you@company.com" autoComplete="email" />
              <Field id="login-pw" icon={<Lock className="w-4 h-4" />} label="Password" type="password"
                value={loginPw} onChange={setLoginPw} placeholder="••••••••" autoComplete="current-password" />
              <div className="flex items-center justify-between text-[13px]">
                <label className="flex items-center gap-2 text-[#0E1F1A]/70">
                  <input type="checkbox" className="accent-[#0B3B2E]" /> Remember me
                </label>
                <a href="#" className="text-[#0B3B2E] hover:underline">Forgot password?</a>
              </div>
              <SubmitButton busy={busy} label="Continue" />
              <p className="text-center text-[12px] text-[#0E1F1A]/55">
                Protected by 256-bit encryption. Vector FX is authorised in the UK.
              </p>
            </form>
          </TabsContent>

          <TabsContent value="signup" className="pt-6">
            <form onSubmit={handleSignup} className="space-y-4">
              <Field id="su-name" icon={<UserIcon className="w-4 h-4" />} label="Full name" type="text"
                value={suName} onChange={setSuName} placeholder="Alex Morgan" autoComplete="name" />
              <Field id="su-email" icon={<Mail className="w-4 h-4" />} label="Email" type="email"
                value={suEmail} onChange={setSuEmail} placeholder="you@company.com" autoComplete="email" />
              <Field id="su-pw" icon={<Lock className="w-4 h-4" />} label="Password" type="password"
                value={suPw} onChange={setSuPw} placeholder="Minimum 6 characters" autoComplete="new-password" />
              <SubmitButton busy={busy} label="Get started" />
              <p className="text-center text-[12px] text-[#0E1F1A]/55">
                By continuing you agree to our Terms &amp; Privacy Notice.
              </p>
            </form>
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}

function Field({ id, icon, label, type, value, onChange, placeholder, autoComplete }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id} className="text-[13px] text-[#0E1F1A]/70">{label}</Label>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#0B3B2E]/50">{icon}</span>
        <Input
          id={id}
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          className="pl-9 h-11 bg-[#F5F1E8] border-[#0B3B2E]/20 focus-visible:ring-[#0B3B2E]/30 focus-visible:border-[#0B3B2E]"
        />
      </div>
    </div>
  );
}

function SubmitButton({ busy, label }) {
  return (
    <Button
      type="submit"
      disabled={busy}
      className="w-full rounded-full h-11 bg-[#0B3B2E] text-[#F5F1E8] hover:bg-[#12503F] font-medium disabled:opacity-70"
    >
      {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Please wait</>
        : <>{label} <ArrowUpRight className="w-4 h-4 ml-1" /></>}
    </Button>
  );
}
